import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Filter,
  Inbox,
  Mail,
  Phone,
  RefreshCw,
  Search,
  UserRound,
} from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { FormPortalHeader } from "./FormPortalHeader";

const leadStatuses = [
  "New Inquiry",
  "Discovery Email Sent",
  "Discovery Scheduled",
  "Discovery Completed",
  "Proposal Approved to Send",
  "Proposal Draft",
  "Proposal Sent",
  "Proposal Viewed",
  "Proposal Accepted",
  "Proposal Declined",
  "Proposal Revision Requested",
  "Agreement Sent",
  "Agreement Viewed",
  "Agreement Signed",
  "Payment Pending",
  "Invoice Created",
  "Invoice Sent",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Paid In Full",
  "Payment Received",
  "Payment Failed",
  "Refunded",
  "Welcome Sent",
  "Onboarding Sent",
  "Onboarding Complete",
  "Kickoff Call Scheduled",
  "Kickoff Call Confirmed",
  "Kickoff Call Completed",
  "Project Active",
  "Awaiting Client Content",
  "On Hold",
  "Build In Progress",
  "Internal Review",
  "Awaiting Client Review",
  "Client Review",
  "Revision Requested",
  "Final Approval",
  "Project Complete",
  "Delivered",
  "Follow-Up Sent",
  "Testimonial Requested",
  "Referral Invitation Sent",
  "Archived",
] as const;

type LeadRecord = {
  id: string;
  leadId: string;
  source: string;
  status: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  eventType: string;
  eventDate: string;
  serviceInterest: string;
  budget: string;
  timeline: string;
  notes: string;
  referralSource: string;
  marketingSource: string;
  activityNotes: string;
  discoveryEmailSentAt: string | null;
  rawPayload?: string | null;
  journeyStatus?: string;
  proposalStatus?: string;
  agreementStatus?: string;
  paymentStatus?: string;
  projectStatus?: string;
  nextStep?: string;
  createdAt: string;
  updatedAt: string;
};

type LeadStatusEvent = {
  id: string;
  leadId: string;
  fromStatus: string | null;
  toStatus: string;
  action: string;
  actorEmail: string | null;
  notes: string | null;
  createdAt: string;
};

type JourneyEvent = {
  id: string;
  leadId: string;
  proposalId?: string | null;
  agreementId?: string | null;
  paymentId?: string | null;
  activityType: string;
  notes: string;
  relatedLink: string;
  createdAt: string;
};

type JourneyDraft = {
  subject: string;
  textContent: string;
};

type LeadDetailResponse = {
  lead: LeadRecord;
  statusEvents: LeadStatusEvent[];
  journeyEvents?: JourneyEvent[];
  proposal?: { proposal: { id: string; proposalNumber: string; token: string; packageName: string; selectedServices: string; recommendedAddons: string; totalCents: number; depositCents: number; balanceDueCents: number }; lineItems: Array<{ id: string; name: string; description: string; amountCents: number }> } | null;
  agreement?: {
    id: string;
    proposalId: string;
    leadId: string;
    status: string;
    token: string;
    provider: string;
    agreementUrl: string;
    sentAt: string | null;
    viewedAt: string | null;
    signedAt: string | null;
    signerName: string | null;
    signerEmail: string | null;
  } | null;
  payment?: {
    id: string;
    proposalId: string;
    agreementId: string | null;
    leadId: string;
    status: string;
    invoiceStatus: string;
    token: string;
    paymentMode: string;
    currency: string;
    stripeCheckoutUrl: string | null;
    subtotalCents: number;
    discountCents: number;
    depositCents: number;
    totalCents: number;
    amountPaidCents: number;
    amountRemainingCents: number;
    taxCents: number;
    taxPercentage: number;
    taxLabel: string;
    dueDate: string | null;
    finalDueDate: string | null;
    paidAt: string | null;
    refundedAt: string | null;
    failedAt: string | null;
    cancelledAt: string | null;
  } | null;
  automation?: {
    welcomeDraft: JourneyDraft | null;
    onboardingDraft: JourneyDraft | null;
  };
};

export function AdminLeadsPage() {
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    document.title = "Leads | Kreatethedate Forms Portal";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Private Kreatethedate lead intake dashboard for website inquiries.");
    void loadLeads();
  }, []);

  async function loadLeads() {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/admin/leads", { credentials: "include" });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as { leads: LeadRecord[] };
      setLeads(data.leads || []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load leads.");
    } finally {
      setLoading(false);
    }
  }

  const filteredLeads = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return leads.filter((lead) => {
      const matchesStatus = statusFilter === "All" || lead.status === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        [
          lead.leadId,
          lead.fullName,
          lead.email,
          lead.phone,
          lead.eventType,
          lead.eventDate,
          lead.serviceInterest,
          lead.referralSource,
          lead.marketingSource,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [leads, query, statusFilter]);

  const newLeads = leads.filter((lead) => lead.status === "New Inquiry").length;
  const activeLeads = leads.filter((lead) => !["Project Complete", "Delivered", "Archived"].includes(lead.status)).length;
  const archivedLeads = leads.filter((lead) => lead.status === "Archived").length;

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader />
      <AdminHero
        eyebrow="Lead Intake Engine"
        title="Website Inquiry Leads"
        description="Review lead records created from website inquiries and follow the full Kreatethedate journey from discovery through proposal, agreement, payment, welcome, and onboarding."
      />

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <div className="grid gap-4 md:grid-cols-3">
            <MetricCard icon={Inbox} label="Total Leads" value={leads.length} />
            <MetricCard icon={Mail} label="New Inquiries" value={newLeads} />
            <MetricCard icon={CheckCircle2} label="Active Leads" value={activeLeads} />
          </div>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-col gap-4 border-b border-[#D94A16]/16 pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Admin Command Center</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#050505]">Leads Inbox</h2>
                <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#5c5246]">
                  Website inquiries appear here after they are submitted to the Forms Portal intake endpoint.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void loadLeads()}
                className="admin-secondary-button focus-ring"
                title="Refresh the lead inbox"
              >
                <RefreshCw className="size-4" aria-hidden="true" />
                Refresh
              </button>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_280px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#D94A16]" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
                  placeholder="Search by name, email, service, event, or source"
                />
              </label>
              <label className="relative block">
                <Filter className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#D94A16]" aria-hidden="true" />
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-bold"
                >
                  <option value="All">All statuses</option>
                  {leadStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {error ? <AlertMessage message={error} /> : null}
            {loading ? <EmptyState title="Loading leads" message="Checking the intake engine for new website inquiries." /> : null}
            {!loading && !error && !filteredLeads.length ? (
              <EmptyState
                title="No leads found"
                message={
                  archivedLeads
                    ? "Adjust filters to view archived or completed leads."
                    : "Your first inquiry will appear here after a website form submission or manual lead entry."
                }
              />
            ) : null}

            <div className="mt-5 grid gap-4">
              {filteredLeads.map((lead) => (
                <LeadCard key={lead.id} lead={lead} />
              ))}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

export function AdminLeadDetailPage() {
  const { leadId } = useParams();
  const [lead, setLead] = useState<LeadRecord | null>(null);
  const [statusEvents, setStatusEvents] = useState<LeadStatusEvent[]>([]);
  const [journeyEvents, setJourneyEvents] = useState<JourneyEvent[]>([]);
  const [proposalRecord, setProposalRecord] = useState<LeadDetailResponse["proposal"]>(null);
  const [agreementRecord, setAgreementRecord] = useState<LeadDetailResponse["agreement"]>(null);
  const [paymentRecord, setPaymentRecord] = useState<LeadDetailResponse["payment"]>(null);
  const [automation, setAutomation] = useState<LeadDetailResponse["automation"]>({ welcomeDraft: null, onboardingDraft: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("New Inquiry");
  const [notes, setNotes] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [kickoffAt, setKickoffAt] = useState("");

  useEffect(() => {
    document.title = "Lead Details | Kreatethedate Forms Portal";
    if (leadId) void loadLead(leadId);
  }, [leadId]);

  if (!leadId) return <Navigate to="/admin/leads" replace />;

  async function loadLead(targetLeadId: string) {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`/api/admin/leads/${targetLeadId}`, { credentials: "include" });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as LeadDetailResponse;
      setLead(data.lead);
      setStatus(data.lead.status);
      setStatusEvents(data.statusEvents || []);
      setJourneyEvents(data.journeyEvents || []);
      setProposalRecord(data.proposal || null);
      setAgreementRecord(data.agreement || null);
      setPaymentRecord(data.payment || null);
      setAutomation(data.automation || { welcomeDraft: null, onboardingDraft: null });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load lead.");
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submitLeadUpdate(status, notes);
  }

  async function submitLeadUpdate(
    nextStatus: string,
    note = "",
    scheduledFor = "",
    workflowAction?: "send_welcome_email" | "send_onboarding_email",
  ) {
    if (!lead) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`/api/admin/leads/${lead.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: nextStatus,
          notes: note,
          scheduledAt: scheduledFor || undefined,
          workflowAction,
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as { lead: LeadRecord; statusEvents: LeadStatusEvent[] };
      setLead(data.lead);
      setStatus(data.lead.status);
      setStatusEvents(data.statusEvents || []);
      setNotes("");
      await loadLead(data.lead.id);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update lead status.");
    } finally {
      setSaving(false);
    }
  }

  const timelineFeed = useMemo(() => {
    return [...statusEvents.map((event) => ({
      id: `status-${event.id}`,
      kind: "Status" as const,
      title: event.toStatus,
      notes: event.notes || (event.fromStatus ? `${event.fromStatus} → ${event.toStatus}` : event.action),
      link: "",
      createdAt: event.createdAt,
    })), ...journeyEvents.map((event) => ({
      id: `journey-${event.id}`,
      kind: "Journey" as const,
      title: event.activityType,
      notes: event.notes || "Journey activity recorded.",
      link: event.relatedLink,
      createdAt: event.createdAt,
    }))].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [journeyEvents, statusEvents]);

  const onboardingChecklist = [
    "Intake form received",
    "Assets requested",
    "Assets received",
    "Kickoff scheduled",
    "Build started",
    "Client review",
    "Revisions",
    "Final approval",
    "Delivery complete",
  ];
  const canSendJourneyEmails = Boolean(proposalRecord?.proposal && paymentRecord);

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <FormPortalHeader />
      <AdminHero
        eyebrow="Lead Detail"
        title={lead?.fullName || "Lead Details"}
        description="Review the captured inquiry details and track the current proposal, agreement, payment, welcome, and onboarding journey for this client."
      />

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <Link to="/admin/leads" className="focus-ring inline-flex w-fit items-center gap-2 rounded-lg text-sm font-bold text-[#D94A16]">
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back to Leads
          </Link>

          {error ? <AlertMessage message={error} /> : null}
          {loading ? <EmptyState title="Loading lead" message="Opening the saved inquiry record." /> : null}
          {!loading && !lead ? <EmptyState title="Lead not found" message="This lead is no longer available in the intake engine. Return to the leads list and open another record." /> : null}

          {lead ? (
            <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
              <div className="grid gap-6">
                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Lead Snapshot" eyebrow={lead.leadId} />
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <DetailItem icon={UserRound} label="Client Name" value={lead.fullName} />
                    <DetailItem icon={Mail} label="Email" value={lead.email} />
                    <DetailItem icon={Phone} label="Phone" value={lead.phone} />
                    <DetailItem icon={CalendarDays} label="Event Date" value={formatDate(lead.eventDate)} />
                    <DetailItem label="Event Type" value={lead.eventType} />
                    <DetailItem label="Service Interest" value={lead.serviceInterest} />
                    <DetailItem label="Budget" value={lead.budget} />
                    <DetailItem label="Timeline" value={lead.timeline} />
                    <DetailItem label="Referral Source" value={lead.referralSource} />
                    <DetailItem label="Marketing Source" value={lead.marketingSource} />
                    <DetailItem label="Discovery Email Sent" value={formatDateTime(lead.discoveryEmailSentAt || "")} />
                    <DetailItem label="Journey Status" value={lead.journeyStatus || lead.status} />
                    <DetailItem label="Proposal Status" value={lead.proposalStatus || "Not Started"} />
                    <DetailItem label="Agreement Status" value={lead.agreementStatus || "Not Started"} />
                    <DetailItem label="Payment Status" value={lead.paymentStatus || "Not Started"} />
                    <DetailItem label="Project Status" value={lead.projectStatus || lead.status} />
                    <DetailItem label="Next Step" value={lead.nextStep || "Discovery Call"} />
                  </div>
                </section>

                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Inquiry Notes" eyebrow="Captured Website Context" />
                  <p className="mt-5 whitespace-pre-wrap rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] p-4 text-sm font-semibold leading-7 text-[#332a1f]">
                    {lead.notes || "No additional notes were provided."}
                  </p>
                </section>

                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Activity Notes" eyebrow="Lead Workflow Activity" />
                  <p className="mt-5 whitespace-pre-wrap rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] p-4 text-sm font-semibold leading-7 text-[#332a1f]">
                    {lead.activityNotes || "No activity notes have been recorded yet."}
                  </p>
                </section>

                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Client Journey Timeline" eyebrow="Discovery Through Delivery" />
                  <div className="mt-5 grid gap-3">
                    {timelineFeed.length ? (
                      timelineFeed.map((item) => (
                        <article key={item.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="font-display text-2xl font-bold text-[#050505]">{item.title}</p>
                              <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">
                                {formatDateTime(item.createdAt)} · {item.kind}
                              </p>
                            </div>
                            {item.link ? (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noreferrer"
                                className="focus-ring inline-flex min-h-10 items-center justify-center rounded-lg bg-[#050505] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                              >
                                Open Link
                              </a>
                            ) : null}
                          </div>
                          <p className="mt-3 text-sm font-semibold leading-6 text-[#332a1f]">{item.notes || "Journey event recorded."}</p>
                        </article>
                      ))
                    ) : (
                      <p className="rounded-lg border border-dashed border-[#D94A16]/18 bg-[#fffdf8] px-4 py-4 text-sm font-semibold text-[#5c5246]">
                        Journey events will appear here as the lead moves from inquiry through delivery.
                      </p>
                    )}
                  </div>
                </section>

                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Automation Engine" eyebrow="Discovery, Welcome, Onboarding, Kickoff" />
                  <div className="mt-5 grid gap-6 xl:grid-cols-2">
                    <div className="grid gap-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Discovery Call Workflow</p>
                      <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
                        Scheduled For
                        <input
                          type="datetime-local"
                          value={scheduledAt}
                          onChange={(event) => setScheduledAt(event.target.value)}
                          className="form-field focus-ring min-h-11 rounded-lg px-4 text-sm font-semibold"
                        />
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Discovery Scheduled", "Discovery call scheduled.", scheduledAt)}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg bg-[#050505] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                        >
                          Schedule Discovery
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Discovery Completed", "Discovery call completed by admin.")}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border border-[#D94A16]/24 px-4 text-xs font-black uppercase tracking-[0.12em] text-[#332a1f]"
                        >
                          Mark Complete
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Welcome Email Automation</p>
                      <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                        <p className="text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">Draft Preview</p>
                        <p className="mt-2 font-display text-2xl font-bold text-[#050505]">{automation?.welcomeDraft?.subject || "Welcome draft not available yet"}</p>
                        <p className="mt-3 text-sm font-semibold leading-7 text-[#5c5246]">
                          {automation?.welcomeDraft?.textContent || "A welcome draft will appear once agreement and payment are available."}
                        </p>
                        <p className="mt-3 text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">
                          Linked Records: {proposalRecord?.proposal.proposalNumber || "No proposal"} · {agreementRecord?.status || "No agreement"} · {paymentRecord?.status || "No payment"}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Welcome Sent", "Welcome email reviewed and sent.", "", "send_welcome_email")}
                          disabled={!canSendJourneyEmails}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg bg-[#050505] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B] disabled:opacity-50"
                        >
                          Send Welcome Email
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Onboarding Sent", "Onboarding email sent with next steps.", "", "send_onboarding_email")}
                          disabled={!canSendJourneyEmails}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border border-[#D94A16]/24 px-4 text-xs font-black uppercase tracking-[0.12em] text-[#332a1f] disabled:opacity-50"
                        >
                          Send Onboarding Email
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Onboarding Engine</p>
                      <div className="grid gap-2">
                        {onboardingChecklist.map((item) => (
                          <div key={item} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] px-4 py-3 text-sm font-semibold text-[#332a1f]">
                            {item}
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Onboarding Complete", "Onboarding checklist completed.")}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg bg-[#050505] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                        >
                          Mark Onboarding Complete
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Kickoff Call Workflow</p>
                      <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
                        Scheduled For
                        <input
                          type="datetime-local"
                          value={kickoffAt}
                          onChange={(event) => setKickoffAt(event.target.value)}
                          className="form-field focus-ring min-h-11 rounded-lg px-4 text-sm font-semibold"
                        />
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Kickoff Call Scheduled", "Kickoff call scheduled.", kickoffAt)}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg bg-[#050505] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                        >
                          Schedule Kickoff
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Kickoff Call Confirmed", "Kickoff call confirmed with client.")}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border border-[#D94A16]/24 px-4 text-xs font-black uppercase tracking-[0.12em] text-[#332a1f]"
                        >
                          Confirm Kickoff
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Kickoff Call Completed", "Kickoff call completed and project ready.")}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border border-[#D94A16]/24 px-4 text-xs font-black uppercase tracking-[0.12em] text-[#332a1f]"
                        >
                          Complete Kickoff
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Project Active", "Project started after kickoff completion.")}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border border-[#D94A16]/24 px-4 text-xs font-black uppercase tracking-[0.12em] text-[#332a1f]"
                        >
                          Start Project
                        </button>
                        <button
                          type="button"
                          onClick={() => void submitLeadUpdate("Delivered", "Project delivered to the client.")}
                          className="focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border border-[#D94A16]/24 px-4 text-xs font-black uppercase tracking-[0.12em] text-[#332a1f]"
                        >
                          Mark Delivered
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Raw Submission Payload" eyebrow="Reference Only" />
                  <pre className="mt-5 max-h-[360px] overflow-auto rounded-lg border border-[#D94A16]/18 bg-[#050505] p-4 text-xs leading-6 text-[#fff9ef]">
                    {formatRawPayload(lead.rawPayload)}
                  </pre>
                </section>
              </div>

              <aside className="grid h-fit gap-6 lg:sticky lg:top-24">
                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Lead Status" eyebrow="Journey Status Tracking" />
                  <p className="mt-4 inline-flex rounded-full bg-[#050505] px-4 py-2 text-sm font-black text-[#FF6A2B]">{lead.status}</p>
                  <form onSubmit={updateStatus} className="mt-5 grid gap-4">
                    <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
                      Update Status
                      <select
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                        className="form-field focus-ring min-h-11 rounded-lg px-4 text-sm font-bold"
                      >
                        {leadStatuses.map((statusOption) => (
                          <option key={statusOption} value={statusOption}>
                            {statusOption}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
                      Status Note
                      <textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        className="form-field focus-ring min-h-28 rounded-lg px-4 py-3 text-sm font-semibold"
                        placeholder="Optional internal note for the status history"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={saving || status === lead.status}
                      className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                    >
                      {saving ? "Saving..." : "Save Status"}
                    </button>
                  </form>
                </section>

                <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                  <SectionTitle title="Status History" eyebrow={`${statusEvents.length} event${statusEvents.length === 1 ? "" : "s"}`} />
                  <div className="mt-5 grid gap-3">
                    {statusEvents.map((event) => (
                      <article key={event.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                        <p className="text-sm font-black text-[#050505]">{event.toStatus}</p>
                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[#D94A16]">{formatDateTime(event.createdAt)}</p>
                        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">
                          {event.fromStatus ? `${event.fromStatus} to ${event.toStatus}` : event.action}
                        </p>
                        {event.notes ? <p className="mt-2 text-sm font-semibold leading-6 text-[#332a1f]">{event.notes}</p> : null}
                      </article>
                    ))}
                  </div>
                </section>
              </aside>
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function AdminHero({ description, eyebrow, title }: { description: string; eyebrow: string; title: string }) {
  return (
    <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
      <div className="relative mx-auto grid max-w-7xl gap-5">
        <p className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
          <ClipboardList className="size-4" aria-hidden="true" />
          {eyebrow}
        </p>
        <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{title}</h1>
        <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">{description}</p>
      </div>
    </section>
  );
}

function LeadCard({ lead }: { lead: LeadRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1fr_.8fr_.7fr_auto] lg:items-center">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-2xl font-bold text-[#050505]">{lead.fullName}</h3>
                  <span className="rounded-full bg-[#050505] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{lead.status}</span>
                </div>
                <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{lead.email || "No email"} {lead.phone ? `| ${lead.phone}` : ""}</p>
                <p className="mt-2 text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">
                  Journey: {lead.journeyStatus || lead.status} {lead.nextStep ? `• Next: ${lead.nextStep}` : ""}
                </p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">
                  Open the full lead timeline to review status history, notes, proposal and payment progress, and next
                  admin actions.
                </p>
              </div>
      <div className="text-sm font-bold leading-6 text-[#332a1f]">
        <p>{lead.serviceInterest || "Service not set"}</p>
        <p className="mt-1 text-[#5c5246]">{lead.eventType || "Event type not set"}</p>
      </div>
      <div className="text-sm font-bold leading-6 text-[#5c5246]">
        <p>{formatDate(lead.eventDate)}</p>
        <p className="mt-1 text-[#332a1f]">{lead.leadId}</p>
      </div>
      <Link
        to={`/admin/leads/${lead.id}`}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]"
        title={`Open the full timeline for ${lead.fullName}`}
      >
        View Lead
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function MetricCard({ icon: Icon, label, value }: { icon: typeof Inbox; label: string; value: number }) {
  const description =
    label === "Total Leads"
      ? "All records captured from website inquiries and admin-created leads."
      : label === "New Inquiries"
        ? "Fresh leads that have not moved beyond the first intake stage."
        : "Leads still active in the journey and requiring follow-up.";
  return (
    <article className="rounded-lg border border-[#D94A16]/22 bg-white p-4 shadow-[0_18px_45px_rgba(45,32,8,.08)]" title={description}>
      <Icon className="size-5 text-[#D94A16]" aria-hidden="true" />
      <p className="mt-4 font-display text-3xl font-bold text-[#050505]">{value}</p>
      <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#5c5246]">{label}</p>
      <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">{description}</p>
    </article>
  );
}

function DetailItem({ icon: Icon, label, value }: { icon?: typeof UserRound; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">
        {Icon ? <Icon className="size-4" aria-hidden="true" /> : null}
        {label}
      </p>
      <p className="mt-2 whitespace-pre-wrap text-sm font-bold leading-6 text-[#332a1f]">{value || "Not provided"}</p>
    </div>
  );
}

function SectionTitle({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="border-b border-[#D94A16]/16 pb-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{eyebrow}</p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#050505]">{title}</h2>
    </div>
  );
}

function AlertMessage({ message }: { message: string }) {
  return <p className="mt-5 rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800">{message}</p>;
}

function EmptyState({ message, title }: { message: string; title: string }) {
  return (
    <div className="mt-5 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-6 text-center">
      <p className="font-display text-2xl font-bold text-[#050505]">{title}</p>
      <p className="mt-2 text-sm font-semibold text-[#5c5246]">{message}</p>
    </div>
  );
}

function formatDate(value: string) {
  if (!value) return "Not set";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function formatDateTime(value: string) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatRawPayload(value?: string | null) {
  if (!value) return "No raw payload was stored.";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}
